import { useState, useEffect, useRef } from 'react'
import loadable from '@loadable/component'

// Components
import { Toast } from '@tracklib/kit'

// Loadable
const FormikLoadable = loadable.lib(() => import('formik'))

const errorMessageTranslations = {
  'Unable to log in with provided credentials.': 'Wrong password and/or email',
}

export const renderErrorMessage = (error: string | Array<string>): JSX.Element => {
  const parsedError = Array.isArray(error) ? error.join('. ') : error
  if ((Array.isArray(error) && error.length) || error) {
    return <Toast description={parsedError} centered datatesting="form_error" />
  }
  return <div />
}

type ChildProps<T> = {
  handleSubmit: (updatedValues: T) => Promise<Record<string, unknown>>
  isDone: boolean
  isValid: boolean
  isSubmitting: boolean
  setFieldValue: (name: string, value: unknown) => void
  submitButtonDisabled: boolean
  submitButtonProps: {
    type?: 'button' | 'submit' | 'reset'
    isDone: boolean
    submitButtonDisabled: boolean
    isLoading: boolean
  }
  values: T
  errors: Record<keyof T, string>
  ErrorMessage: () => JSX.Element
}

type FormProps<T> = {
  children: (childProps: ChildProps<T>) => JSX.Element
  onSubmit: (updatedValues: T) => Promise<Record<string, unknown>>
  initialValues: T
  validate?: (values: T) => Partial<Record<keyof T, string | Array<string>>>
  onDone?: (response: Record<string, unknown>, values: T) => void
  formDoneDelay?: number
  className?: string
  enableReinitialize?: boolean
  isInitialValid?: boolean
  alwaysDirty?: boolean
}

function Form<T>({
  children,
  initialValues,
  onSubmit,
  validate,
  onDone,
  formDoneDelay = 500,
  className,
  enableReinitialize,
  isInitialValid,
  alwaysDirty,
}: FormProps<T>): JSX.Element {
  const [isDone, setIsDone] = useState(false)
  const isCancelled = useRef(false)

  // NOTE(Kitti): Used to not updated state if unmounted
  useEffect(() => {
    return () => {
      isCancelled.current = true
    }
  }, [])

  return (
    <FormikLoadable>
      {({ Formik, Form: FormikForm }) => (
        <Formik
          initialValues={initialValues}
          enableReinitialize={enableReinitialize}
          isInitialValid={isInitialValid}
          onSubmit={async (values, { setSubmitting, resetForm, setErrors }) => {
            await setSubmitting(true)

            return onSubmit(values).then(response => {
              if (isCancelled.current) {
                return Promise.resolve()
              }
              setSubmitting(false)
              if (response?.data?.error) {
                setErrors(response.data.error)
              } else {
                setIsDone(true)
                if (formDoneDelay !== -1) {
                  setTimeout(() => {
                    resetForm()
                    setIsDone(false)
                    if (onDone) {
                      onDone(response, values)
                    }
                  }, formDoneDelay)
                }
              }
              return Promise.resolve()
            })
          }}
          validate={validate}
        >
          {({ dirty, handleSubmit, isSubmitting, isValid, setFieldValue, values, errors }) => {
            const submitButtonDisabled = isSubmitting || !isValid || (!dirty && !alwaysDirty) || isDone
            const submitButtonProps = {
              type: 'submit',
              isDone,
              disabled: submitButtonDisabled,
              isLoading: isSubmitting,
            }
            return (
              <FormikForm className={className}>
                {children({
                  handleSubmit,
                  isDone,
                  isValid,
                  isSubmitting,
                  setFieldValue,
                  submitButtonDisabled,
                  submitButtonProps,
                  values,
                  errors,
                  ErrorMessage: () => renderErrorMessage(errorMessageTranslations[errors.non_field_errors]),
                })}
              </FormikForm>
            )
          }}
        </Formik>
      )}
    </FormikLoadable>
  )
}

export default Form
